import React, { useEffect, useState } from 'react'
import { Box, Button, Container, Dialog, DialogContent, DialogTitle, Grid, IconButton, MenuItem, TextField } from '@mui/material'
import { Formik } from 'formik'
import * as Yup from "yup";
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { toast } from 'react-toastify';

const ROLE_OPTIONS = [
    { value: 0, label: 'Select role' },
    { value: 1, label: 'Admin' },
    { value: 2, label: 'Cashier' },
    { value: 3, label: 'Accountant' },
    { value: 4, label: 'Store Keeper' },
];

export default function EmployeeDialog({ dialogOpen, setDialogOpen, updateID, setUpdateID }) {
    const [employeeData, setEmployeeData] = useState({
        EmpName: "",
        ContactNo: "",
        role: 0,
    });
    const [isUpdate, setIsUpdate] = useState(false);

    useEffect(() => {
        if (updateID > 0) {
            getEmployeeById();
        }
    }, [updateID]);

    function handleClose() {
        setDialogOpen(false);
        clearData();
        setIsUpdate(false);
        setUpdateID(0);
    }

    function handleChange(e) {
        const target = e.target;
        const value = target.value;
        setEmployeeData({
            ...employeeData,
            [target.name]: value
        });
    }

    function clearData() {
        setEmployeeData({
            EmpName: "",
            ContactNo: "",
            role: 0,
        });
    }

    function saveEmployee() {
        if (isUpdate) {
            const updateModel = {
                empid: updateID,
                EmpName: employeeData.EmpName.trim(),
                ContactNo: employeeData.ContactNo,
                role: parseInt(employeeData.role),
            };
            axios.put('https://serverdemo.ccsoftwares.com/Employees/updateEmployee', updateModel).then((res) => {
                if (res.data.affectedRows > 0) {
                    toast.success("Employee updated successfully!");
                }
                handleClose();
            }).catch(() => {
                toast.error("Employee update failed");
            });
        } else {
            const saveModel = {
                EmpName: employeeData.EmpName.trim(),
                ContactNo: employeeData.ContactNo,
                role: parseInt(employeeData.role),
            };
            axios.post('https://serverdemo.ccsoftwares.com/Employees/createEmployee', saveModel).then((res) => {
                if (res.data.employeeId) {
                    toast.success("Employee added successfully!");
                } else {
                    toast.success("Employee added");
                }
                handleClose();
            }).catch(() => {
                toast.error("Employee adding failed");
            });
        }
    }

    function getEmployeeById() {
        axios.get(`https://serverdemo.ccsoftwares.com/Employees/getEmployee/${updateID}`).then((res) => {
            if (res.data.length > 0) {
                setEmployeeData({
                    EmpName: res.data[0].EmpName,
                    ContactNo: res.data[0].ContactNo,
                    role: res.data[0].role,
                });
                setIsUpdate(true);
            }
        }).catch(() => {
            toast.error("Unable to load employee");
        });
    }

    return (
        <Dialog
            aria-labelledby="customized-dialog-title"
            open={dialogOpen}
            fullWidth={true}
            maxWidth='md'
        >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                {isUpdate ? 'Update Employee' : 'Add Employee'}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon sx={{ color: 'black' }} />
            </IconButton>
            <DialogContent dividers>
                <Container maxWidth={false}>
                    <Formik
                        initialValues={{
                            EmpName: employeeData.EmpName,
                            ContactNo: employeeData.ContactNo,
                            role: employeeData.role,
                        }}
                        validationSchema={
                            Yup.object().shape({
                                EmpName: Yup.string()
                                    .required('Employee Name is required')
                                    .test('no-leading-space', 'Employee Name cannot start with a space', value => value && value.trim().length > 0),
                                ContactNo: Yup.string()
                                    .required('Contact Number is required')
                                    .matches(/^[0-9]{10}$/, 'Contact Number must be exactly 10 digits'),
                                role: Yup.number()
                                    .required('Role is required')
                                    .test('valid-selection', 'Please select a role', value => value !== 0),
                            })
                        }
                        onSubmit={() => saveEmployee()}
                        enableReinitialize
                    >
                        {({
                            errors,
                            handleBlur,
                            handleSubmit,
                            touched
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Box mt={0}>
                                    <Grid container spacing={2}>
                                        <Grid item md={4} xs={12}>
                                            <TextField
                                                fullWidth
                                                error={Boolean(touched.EmpName && errors.EmpName)}
                                                helperText={touched.EmpName && errors.EmpName}
                                                label="Employee Name *"
                                                name='EmpName'
                                                value={employeeData.EmpName}
                                                id='EmpName'
                                                onBlur={handleBlur}
                                                onChange={(e) => handleChange(e)}
                                                variant="outlined"
                                                size="small"
                                                type='text'
                                            />
                                        </Grid>
                                        <Grid item md={4} xs={12}>
                                            <TextField
                                                fullWidth
                                                error={Boolean(touched.ContactNo && errors.ContactNo)}
                                                helperText={touched.ContactNo && errors.ContactNo}
                                                label="Contact Number *"
                                                name='ContactNo'
                                                value={employeeData.ContactNo}
                                                id='ContactNo'
                                                onBlur={handleBlur}
                                                onChange={(e) => handleChange(e)}
                                                variant="outlined"
                                                size="small"
                                                type='text'
                                                onKeyDown={(e) => {
                                                    const value = e.target.value;
                                                    if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete') {
                                                        e.preventDefault();
                                                    }
                                                    if (value.length >= 10 && e.key !== 'Backspace' && e.key !== 'Delete') {
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={4} xs={12}>
                                            <TextField
                                                select
                                                fullWidth
                                                error={Boolean(touched.role && errors.role)}
                                                helperText={touched.role && errors.role}
                                                label="Employee Role *"
                                                name='role'
                                                value={employeeData.role}
                                                id='role'
                                                onBlur={handleBlur}
                                                onChange={(e) => handleChange(e)}
                                                variant="outlined"
                                                size="small"
                                            >
                                                {ROLE_OPTIONS.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>
                                    </Grid>
                                    <br />
                                    <Grid container spacing={2}>
                                        <Grid item md={9} xs={12}></Grid>
                                        <Grid item md={3} xs={12}>
                                            <Box display='flex' justifyContent='end' alignItems='center'>
                                                <Button type='submit' variant="contained" sx={{ width: '50%' }}>
                                                    {isUpdate ? 'Update' : 'Save'}
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </Container>
            </DialogContent>
        </Dialog>
    );
}
